import React, { useState, useEffect, useCallback } from 'react';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_calendar.css';
import roleApi from '@services/roleApi';
import Pagination from '@components/common/Pagination';
import AlertModal from '@components/common/AlertModal';
import ConfirmModal from '@components/common/ConfirmModal';

const AccountGroupManagement = () => {
  // 상태 관리
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 페이지네이션 관련 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // 체크박스 상태 관리
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);
  
  // 현재 선택된 그룹
  const [currentGroup, setCurrentGroup] = useState(null);
  
  // 모달 입력값 상태 관리
  const [groupModalInputs, setGroupModalInputs] = useState({
    groupName: '',
    isDuplicateChecked: false,
    isAvailableName: false,
    branch: '신사점', // 기본값으로 신사점 설정
    readPermission: false,
    writePermission: false,
    updatePermission: false,
    deletePermission: false,
    visibility: false
  });
  
  // 권한 관련 상태
  const [availablePermissions, setAvailablePermissions] = useState([
    { id: 'permission-1', name: '권한관리' },
    { id: 'permission-2', name: '데이터관리' },
    { id: 'permission-3', name: '기기관리' },
    { id: 'permission-4', name: '사용자 메뉴 관리' },
    { id: 'permission-5', name: '기기세팅' },
    { id: 'permission-6', name: 'QR 서버 관리' },
    { id: 'permission-7', name: '매출 관리' },
    { id: 'permission-8', name: '매뉴얼 페이지' }
  ]);
  
  const [selectedPermissions, setSelectedPermissions] = useState([
    { id: 'permission-default', name: '소프트웨어 관리' }
  ]);
  
  const [selectedAvailablePermission, setSelectedAvailablePermission] = useState(null);
  const [selectedAddedPermission, setSelectedAddedPermission] = useState(null);
  
  // 다중 선택을 위한 상태 추가
  const [selectedAvailablePermissions, setSelectedAvailablePermissions] = useState([]);
  const [selectedAddedPermissions, setSelectedAddedPermissions] = useState([]);
  
  // 필터 상태
  const [filterType, setFilterType] = useState('latest');
  
  // Spring Data의 sort 파라미터로 변환
  const getSortParam = (filterType) => {
    switch (filterType) {
      case 'name':
        return 'name,asc';
      case 'period':
        return 'createdAt,asc'; // 사용기간순 (오래된 순)
      case 'latest':
      default:
        return 'createdAt,desc'; // 최신순
    }
  };
  
  // 권한 그룹 목록 조회 함수
  const fetchRoleGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Spring Data는 0부터 페이지 번호 시작
      const backendPage = currentPage - 1;
      const sortParam = getSortParam(filterType);
      
      // 실제 API 호출 (백엔드와 연동 시 주석 해제)
      // const response = await roleApi.getRoleGroups(backendPage, pageSize, sortParam);
      // const { content, totalElements, totalPages } = response;
      // setGroups(content);
      // setTotalItems(totalElements);
      // setTotalPages(totalPages);
      
      // 임시로 mock 데이터 사용 (백엔드와 연동 전)
      const mockGroups = [
        {
          id: 1,
          name: 'Admin',
          permissions: ['권한관리', '데이터관리'],
          createdAt: '2023-01-01T00:00:00',
          branch: '신사점'
        },
        {
          id: 2,
          name: 'Operator',
          permissions: ['기기관리', '기기세팅'],
          createdAt: '2023-02-01T00:00:00',
          branch: '강남점'
        },
        {
          id: 3,
          name: 'Manager',
          permissions: ['권한관리', '기기관리', '매출 관리'],
          createdAt: '2023-03-01T00:00:00',
          branch: '신사점'
        },
        {
          id: 4,
          name: 'Basic User',
          permissions: ['매뉴얼 페이지'],
          createdAt: '2023-04-01T00:00:00',
          branch: '강남점'
        },
        {
          id: 5,
          name: 'Support',
          permissions: ['QR 서버 관리', '기기세팅'],
          createdAt: '2023-05-01T00:00:00',
          branch: '신사점'
        }
      ];
      
      // filterType에 따라 정렬
      let sortedGroups = [...mockGroups];
      if (filterType === 'name') {
        sortedGroups.sort((a, b) => a.name.localeCompare(b.name));
      } else if (filterType === 'period') {
        sortedGroups.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      } else {
        // 기본값 - 최신순
        sortedGroups.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }
      
      setGroups(sortedGroups);
      
      // 페이지네이션 정보 설정
      setTotalItems(mockGroups.length);
      setTotalPages(Math.ceil(mockGroups.length / pageSize));
      
    } catch (err) {
      console.error('권한 그룹 목록 조회 실패:', err);
      setError('권한 그룹 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterType]);
  
  // 초기 데이터 로드
  useEffect(() => {
    fetchRoleGroups();
  }, [fetchRoleGroups]);
  
  // 페이지 변경 핸들러
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // 체크박스 전체 선택/해제 핸들러
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    setSelectedItems(e.target.checked ? groups.map(group => group.id) : []);
  };
  
  // 개별 체크박스 핸들러
  const handleItemSelect = (id, e) => {
    if (e.target.checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(item => item !== id));
    }
    
    // 전체 선택 상태 업데이트
    if (e.target.checked && selectedItems.length + 1 === groups.length) {
      setSelectAll(true);
    } else if (!e.target.checked && selectAll) {
      setSelectAll(false);
    }
  };
  
  // 필터 변경 핸들러
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
    setCurrentPage(1); // 필터 변경 시 첫 페이지로 이동
  };
  
  // 그룹 추가 모달 열기 핸들러
  const handleAddGroupClick = () => {
    setGroupModalInputs({
      groupName: '',
      isDuplicateChecked: false,
      isAvailableName: false,
      branch: '신사점',
      readPermission: false,
      writePermission: false,
      updatePermission: false,
      deletePermission: false,
      visibility: false
    });
    setSelectedPermissions([
      { id: 'permission-default', name: '소프트웨어 관리' }
    ]);
    setShowAddModal(true);
  };
  
  // 그룹 수정 모달 열기 핸들러
  const handleEditClick = (group) => {
    setCurrentGroup(group);
    setGroupModalInputs({
      groupName: group.name,
      isDuplicateChecked: true, // 기존 이름은 중복 확인이 필요 없음
      isAvailableName: true,
      branch: group.branch,
      readPermission: group.readPermission || false,
      writePermission: group.writePermission || false,
      updatePermission: group.updatePermission || false,
      deletePermission: group.deletePermission || false,
      visibility: group.visibility || false
    });
    
    // 선택된 권한 설정
    const selected = [
      { id: 'permission-default', name: '소프트웨어 관리' },
      ...group.permissions.map((perm, index) => {
        // 일치하는 권한 ID 찾기
        const foundPermission = availablePermissions.find(p => p.name === perm);
        return foundPermission || { id: `permission-custom-${index}`, name: perm };
      })
    ];
    
    setSelectedPermissions(selected);
    setShowEditModal(true);
  };
  
  // 그룹 삭제 버튼 핸들러
  const handleDeleteClick = (group) => {
    setCurrentGroup(group);
    setConfirmMessage(`${group.name} 그룹을 삭제하시겠습니까?`);
    setConfirmCallback(() => () => {
      // 그룹 삭제 로직
      setGroups(groups.filter(g => g.id !== group.id));
      setAlertMessage('삭제되었습니다.');
      setShowAlertModal(true);
    });
    setShowConfirmModal(true);
  };
  
  // 모달 입력값 변경 핸들러
  const handleGroupInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGroupModalInputs({
      ...groupModalInputs,
      [name]: type === 'checkbox' ? checked : value,
      isDuplicateChecked: name === 'groupName' ? false : groupModalInputs.isDuplicateChecked
    });
  };
  
  // 중복 확인 핸들러
  const handleDuplicateCheck = () => {
    const { groupName } = groupModalInputs;
    
    if (!groupName) {
      setAlertMessage('그룹명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    // 현재 편집 중인 그룹이면 중복 아님
    if (currentGroup && currentGroup.name === groupName) {
      setGroupModalInputs({
        ...groupModalInputs,
        isDuplicateChecked: true,
        isAvailableName: true
      });
      setAlertMessage('사용 가능한 그룹명입니다.');
      setShowAlertModal(true);
      return;
    }
    
    // 중복 확인
    const isDuplicate = groups.some(group => group.name === groupName);
    
    setGroupModalInputs({
      ...groupModalInputs,
      isDuplicateChecked: true,
      isAvailableName: !isDuplicate
    });
    
    if (isDuplicate) {
      setAlertMessage('이미 사용 중인 그룹명입니다. 다른 그룹명을 입력해주세요.');
    } else {
      setAlertMessage('사용 가능한 그룹명입니다.');
    }
    setShowAlertModal(true);
  };
  
  // 권한 선택 핸들러
  const handlePermissionSelect = (type, id) => {
    if (type === 'available') {
      setSelectedAvailablePermission(id);
      setSelectedAddedPermission(null);
    } else {
      setSelectedAddedPermission(id);
      setSelectedAvailablePermission(null);
    }
  };
  
  // 다중 선택 핸들러
  const handlePermissionCheckboxChange = (type, permissionId) => {
    if (type === 'available') {
      setSelectedAvailablePermissions(prev => {
        if (prev.includes(permissionId)) {
          return prev.filter(id => id !== permissionId);
        } else {
          return [...prev, permissionId];
        }
      });
    } else {
      setSelectedAddedPermissions(prev => {
        if (prev.includes(permissionId)) {
          return prev.filter(id => id !== permissionId);
        } else {
          return [...prev, permissionId];
        }
      });
    }
  };
  
  // 다중 추가 핸들러
  const handleMultiAddPermission = () => {
    if (selectedAvailablePermissions.length === 0) return;
    
    const permissionsToAdd = availablePermissions.filter(p => 
      selectedAvailablePermissions.includes(p.id) && 
      !selectedPermissions.some(sp => sp.id === p.id)
    );
    
    if (permissionsToAdd.length > 0) {
      setSelectedPermissions([...selectedPermissions, ...permissionsToAdd]);
      setSelectedAvailablePermissions([]);
    }
  };
  
  // 다중 제거 핸들러
  const handleMultiRemovePermission = () => {
    if (selectedAddedPermissions.length === 0) return;
    
    setSelectedPermissions(selectedPermissions.filter(p => !selectedAddedPermissions.includes(p.id)));
    setSelectedAddedPermissions([]);
  };
  
  // 권한 추가 핸들러
  const handleAddPermission = () => {
    if (!selectedAvailablePermission) return;
    
    const permissionToAdd = availablePermissions.find(p => p.id === selectedAvailablePermission);
    if (!permissionToAdd) return;
    
    // 이미 추가된 권한인지 확인
    const isDuplicate = selectedPermissions.some(p => p.id === permissionToAdd.id);
    if (isDuplicate) return;
    
    setSelectedPermissions([...selectedPermissions, permissionToAdd]);
    setSelectedAvailablePermission(null);
  };
  
  // 권한 제거 핸들러
  const handleRemovePermission = () => {
    if (!selectedAddedPermission || selectedAddedPermission === 'permission-default') return;
    
    setSelectedPermissions(selectedPermissions.filter(p => p.id !== selectedAddedPermission));
    setSelectedAddedPermission(null);
  };
  
  // 모달 제출 핸들러 (추가)
  const handleAddGroupSubmit = () => {
    const { groupName, isDuplicateChecked, isAvailableName, branch, readPermission, writePermission, updatePermission, deletePermission, visibility } = groupModalInputs;
    
    // 유효성 검사
    if (!groupName) {
      setAlertMessage('그룹명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!isDuplicateChecked || !isAvailableName) {
      setAlertMessage('그룹명 중복 확인이 필요합니다.');
      setShowAlertModal(true);
      return;
    }
    
    // 새 그룹 생성
    const newGroup = {
      id: groups.length > 0 ? Math.max(...groups.map(g => g.id)) + 1 : 1,
      name: groupName,
      permissions: selectedPermissions
        .filter(p => p.id !== 'permission-default')
        .map(p => p.name),
      createdAt: new Date().toISOString(),
      branch: branch,
      readPermission: readPermission,
      writePermission: writePermission,
      updatePermission: updatePermission,
      deletePermission: deletePermission,
      visibility: visibility
    };
    
    setGroups([...groups, newGroup]);
    setShowAddModal(false);
    
    // 성공 알림
    setAlertMessage('그룹이 추가되었습니다.');
    setShowAlertModal(true);
  };
  
  // 모달 제출 핸들러 (수정)
  const handleEditGroupSubmit = () => {
    const { groupName, isDuplicateChecked, isAvailableName, branch, readPermission, writePermission, updatePermission, deletePermission, visibility } = groupModalInputs;
    
    // 유효성 검사
    if (!groupName) {
      setAlertMessage('그룹명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!isDuplicateChecked || !isAvailableName) {
      setAlertMessage('그룹명 중복 확인이 필요합니다.');
      setShowAlertModal(true);
      return;
    }
    
    // 그룹 수정
    const nameChanged = currentGroup.name !== groupName;
    
    setGroups(groups.map(group => 
      group.id === currentGroup.id
        ? {
            ...group,
            name: groupName,
            permissions: selectedPermissions
              .filter(p => p.id !== 'permission-default')
              .map(p => p.name),
            branch: branch,
            readPermission: readPermission,
            writePermission: writePermission,
            updatePermission: updatePermission,
            deletePermission: deletePermission,
            visibility: visibility
          }
        : group
    ));
    
    setShowEditModal(false);
    
    // 성공 알림
    const nameChangeText = nameChanged 
      ? `그룹명이 '${currentGroup.name}'에서 '${groupName}'으로 변경되었습니다.` 
      : '';
    
    const message = nameChanged 
      ? `${nameChangeText} 권한이 수정되었습니다.`
      : `${groupName} 그룹의 권한이 수정되었습니다.`;
      
    setAlertMessage(message);
    setShowAlertModal(true);
  };
  
  // 모달 닫기 함수
  const handleCloseAllModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDetailModal(false);
    setShowAlertModal(false);
    setShowConfirmModal(false);
  };
  
  // 알림 모달만 닫는 함수
  const handleCloseAlertModal = () => {
    setShowAlertModal(false);
  };
  
  // 상세정보 모달만 닫는 함수
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
  };
  
  // 확인 모달 확인 버튼 핸들러
  const handleConfirmModalConfirm = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirmModal(false);
  };
  
  // 그룹 상세정보 보기 핸들러
  const handleInfoClick = (group) => {
    setCurrentGroup(group);
    setShowDetailModal(true);
  };
  
  // 그룹 모달 렌더링 함수
  const renderGroupModal = (isEdit) => {
    return (
      <div className="modal-overlay active" id={isEdit ? "edit-group-modal" : "create-theme-modal"}>
        <div className="modal-container">
          <div className="modal-header">
            <h3 className="modal-title">{isEdit ? "계정권한 그룹 수정" : "계정권한 그룹 추가"}</h3>
            <button className="modal-close" onClick={handleCloseAllModals}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="modal-form-group">
              <label className="modal-label">지점</label>
              <div className="modal-select-container">
                <select 
                  className="modal-select" 
                  name="branch"
                  value={groupModalInputs.branch}
                  onChange={handleGroupInputChange}
                >
                  <option value="신사점">신사점</option>
                  <option value="강남점">강남점</option>
                </select>
                <span className="ri-arrow-down-s-line modal-select-arrow"></span>
              </div>
            </div>
            
            <div className="modal-form-group">
              <label className="modal-label">그룹명</label>
              <div className="modal-input-with-button">
                <input 
                  type="text" 
                  className="modal-input" 
                  id="theme-name"
                  name="groupName"
                  value={groupModalInputs.groupName}
                  onChange={handleGroupInputChange}
                  placeholder="그룹명을 입력하세요"
                />
                <button 
                  className="modal-btn modal-btn-secondary" 
                  id="check-duplicate-btn"
                  onClick={handleDuplicateCheck}
                >
                  중복확인
                </button>
              </div>
            </div>
            
            <div className="modal-form-group toggle-group">
              <div className="toggle-label">읽기 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="read-permission" 
                    name="readPermission"
                    checked={groupModalInputs.readPermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            <div className="modal-form-group toggle-group">
              <div className="toggle-label">쓰기 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="write-permission" 
                    name="writePermission"
                    checked={groupModalInputs.writePermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            <div className="modal-form-group toggle-group">
              <div className="toggle-label">수정 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="update-permission" 
                    name="updatePermission"
                    checked={groupModalInputs.updatePermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            <div className="modal-form-group toggle-group">
              <div className="toggle-label">삭제 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="delete-permission" 
                    name="deletePermission"
                    checked={groupModalInputs.deletePermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            <div className="modal-form-group toggle-group">
              <div className="toggle-label">Visibility</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="visibility" 
                    name="visibility"
                    checked={groupModalInputs.visibility}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            <div className="modal-form-group">
              <label className="modal-label">권한 설정</label>
              <div className="permission-transfer-container">
                <div className="permission-box">
                  <div className="permission-header">접근 권한 목록</div>
                  <div className="permission-list" id="available-permissions">
                    {availablePermissions.map(permission => (
                      <div 
                        key={`${permission.id}${isEdit ? '-edit' : ''}`}
                        className={`permission-item ${selectedAvailablePermissions.includes(permission.id) ? 'selected' : ''}`}
                      >
                        <input 
                          type="checkbox" 
                          id={`${permission.id}${isEdit ? '-edit' : ''}`} 
                          name={`permission-select${isEdit ? '-edit' : ''}`} 
                          className="permission-checkbox"
                          checked={selectedAvailablePermissions.includes(permission.id)}
                          onChange={() => handlePermissionCheckboxChange('available', permission.id)}
                        />
                        <label htmlFor={`${permission.id}${isEdit ? '-edit' : ''}`}>{permission.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="permission-controls">
                  <button 
                    id="add-permission-btn" 
                    className="permission-control-btn"
                    onClick={handleMultiAddPermission}
                  >
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <button 
                    id="remove-permission-btn" 
                    className="permission-control-btn"
                    onClick={handleMultiRemovePermission}
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                </div>
                
                <div className="permission-box">
                  <div className="permission-header">추가된 권한</div>
                  <div className="permission-list" id="selected-permissions">
                    {selectedPermissions.map(permission => (
                      <div 
                        key={`${permission.id}-selected${isEdit ? '-edit' : ''}`}
                        className={`permission-item ${selectedAddedPermissions.includes(permission.id) ? 'selected' : ''}`}
                      >
                        <input 
                          type="checkbox" 
                          id={`${permission.id}-selected${isEdit ? '-edit' : ''}`} 
                          name={`selected-permission${isEdit ? '-edit' : ''}`} 
                          className="permission-checkbox"
                          checked={selectedAddedPermissions.includes(permission.id)}
                          onChange={() => handlePermissionCheckboxChange('selected', permission.id)}
                        />
                        <label htmlFor={`${permission.id}-selected${isEdit ? '-edit' : ''}`}>{permission.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="modal-btn modal-btn-cancel" 
              onClick={handleCloseAllModals}
            >
              취소
            </button>
            <button 
              className="modal-btn modal-btn-primary" 
              onClick={isEdit ? handleEditGroupSubmit : handleAddGroupSubmit}
            >
              확인
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // 일괄 삭제 핸들러
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return;
    
    setConfirmMessage(`선택한 ${selectedItems.length}개의 그룹을 삭제하시겠습니까?`);
    setConfirmCallback(() => () => {
      setGroups(groups.filter(group => !selectedItems.includes(group.id)));
      setSelectedItems([]);
      setSelectAll(false);
      setAlertMessage('선택한 그룹이 삭제되었습니다.');
      setShowAlertModal(true);
    });
    setShowConfirmModal(true);
  };

  return (
    <>
      <div className="page-title-container">
        <h1 className="page-title">계정권한 그룹</h1>
        <div className="page-title-icon">
          <a href="#" id="add-theme-btn" title="계정권한 그룹 추가" onClick={handleAddGroupClick}>
            <i className="ri-add-circle-line"></i>
          </a>
        </div>
      </div>
      
      <div className="page-container">
        {/* 필터 드롭다운 */}
        <div className="filter-dropdown-container-row">
          {/* 일괄 삭제 버튼 */}
          <div className={`bulk-delete-container ${selectedItems.length >= 2 ? 'active' : ''}`}>
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              일괄 삭제 ({selectedItems.length})
            </button>
          </div>

          <div className="filter-dropdown">
            <select 
              id="group-filter"
              value={filterType}
              onChange={handleFilterChange}
            >
              <option value="latest">최신순</option>
              <option value="name">이름순</option>
              <option value="period">사용기간순</option>
            </select>
          </div>
        </div>
        
        {/* 그룹 테이블 */}
        <div className="common-table-container">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>데이터를 불러오는 중입니다...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button 
                className="retry-button"
                onClick={fetchRoleGroups}
              >
                다시 시도
              </button>
            </div>
          ) : (
            <table className="common-table">
              <thead>
                <tr>
                  <th>
                    <div className="checkbox-container">
                      <input 
                        type="checkbox" 
                        id="select-all"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="select-all" className="custom-checkbox"></label>
                    </div>
                  </th>
                  <th>NO</th>
                  <th>지점</th>
                  <th>그룹</th>
                  <th>상세정보</th>
                  <th>수정</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data-message">
                      등록된 계정권한 그룹이 없습니다.
                    </td>
                  </tr>
                ) : (
                  groups.map((group, index) => (
                    <tr key={group.id}>
                      <td>
                        <div className="checkbox-container">
                          <input 
                            type="checkbox" 
                            id={`group-${group.id}`}
                            checked={selectedItems.includes(group.id)}
                            onChange={(e) => handleItemSelect(group.id, e)}
                          />
                          <label htmlFor={`group-${group.id}`} className="custom-checkbox"></label>
                        </div>
                      </td>
                      <td>{index + 1}</td>
                      <td>{group.branch || '신사점'}</td>
                      <td>{group.name}</td>
                      <td>
                        <button 
                          className="info-btn"
                          onClick={() => handleInfoClick(group)}
                        >
                          <i className="ri-information-line"></i>
                        </button>
                      </td>
                      <td>
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditClick(group)}
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                      </td>
                      <td>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteClick(group)}
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* 페이지네이션 */}
        {!loading && !error && totalPages > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageGroupSize={5}
          />
        )}
      </div>
      
      {/* 그룹 추가 모달 */}
      {showAddModal && renderGroupModal(false)}
      
      {/* 그룹 수정 모달 */}
      {showEditModal && currentGroup && renderGroupModal(true)}
      
      {/* 그룹 상세정보 모달 */}
      {showDetailModal && currentGroup && (
        <div className="modal-overlay active" id="group-detail-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">계정권한 그룹 상세정보</h3>
              <button className="modal-close" onClick={handleCloseDetailModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-item-container">
                <span className="detail-label">지점</span>
                <span className="detail-content">{currentGroup.branch || '신사점'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">그룹명</span>
                <span className="detail-content">{currentGroup.name}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">권한</span>
                <span className="detail-content">
                  {[
                    currentGroup.readPermission ? '읽기' : null,
                    currentGroup.writePermission ? '쓰기' : null,
                    currentGroup.updatePermission ? '수정' : null,
                    currentGroup.deletePermission ? '삭제' : null
                  ].filter(Boolean).join(', ') || '없음'}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">Visibility</span>
                <span className="detail-content">
                  {currentGroup.visibility ? 'Y' : 'N'}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">접근 권한</span>
                <span className="detail-content">
                  {currentGroup.permissions && currentGroup.permissions.length > 0
                    ? currentGroup.permissions.join(', ')
                    : '없음'}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">생성일</span>
                <span className="detail-content">
                  {currentGroup.createdAt
                    ? new Date(currentGroup.createdAt).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      }).replace(/\. /g, '-').replace(/\.$/, '')
                    : '-'}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-primary" 
                onClick={handleCloseDetailModal}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 알림/확인 모달 */}
      <AlertModal
        isOpen={showAlertModal}
        message={alertMessage}
        onClose={handleCloseAlertModal}
      />
      
      <ConfirmModal
        isOpen={showConfirmModal}
        message={confirmMessage}
        onConfirm={handleConfirmModalConfirm}
        onCancel={handleCloseAllModals}
      />
    </>
  );
};

export default AccountGroupManagement;